import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';

// Actions for updating data after polling
import { fetchAllControlsAndResponses } from 'api/control';
import {
  fetchAllDevices,
  allowedDevicesProperties,
  getConfigOptions,
  allowedConfigurationProperties,
  fetchDeviceBySFDI
} from 'api/device';
import {
  setConfigurationOptions,
  setDevices,
  setIndividualDevice
} from 'reducers/device/actions';
import { setCurves, setIndividualCurve } from 'reducers/curve/actions';
import { setDeletedControls, setControls } from 'reducers/control/actions';
import {
  fetchAllCurves,
  fetchCurveById,
  allowedCurvesProperties
} from 'api/curve';
import {
  fetchAllTopoNodes,
  fetchTopoNodeById,
  fetchTopoNodeDefaultControl,
  allowedTopoNodesProperties
} from 'api/toponode';
import {
  fetchAllIndeNodes,
  allowedIndeNodesProperties,
  fetchIndeNodeById,
  fetchIndeNodeDefaultControl
} from 'api/indenode';
import {
  setIndeNodes,
  setIndeNodeData,
  setIndeNodeDefaultControl
} from 'reducers/indenode/actions';
import {
  fetchAllAggregators,
  fetchAggregatorById,
  allowedAggregatorsProperties
} from 'api/aggregator';
import {
  setAggregators,
  setIndividualAggregator
} from 'reducers/aggregator/actions';
import { updateLastUpdatedTime } from 'reducers/polling/actions';
import { filterForAllowedProperties } from 'utils/format';
import {
  setTopoNodes,
  setTopoNodeData,
  setTopoNodeDefaultControl
} from 'reducers/toponode/actions';

const reduxObjectNames = {
  TOPONODES: 'topoNodes',
  INDENODES: 'indeNodes',
  AGGREGATORS: 'aggregators',
  CONTROLS: 'controls',
  DELETED_CONTROLS: 'deletedControls',
  CURVES: 'curves',
  DEVICES: 'devices',
  POLLING: 'polling',
  CONFIGURATION: 'configuration'
};

class Polling extends Component {
  constructor(props) {
    super(props);
    this.intervals = {};
    const {
      setTopoNodes,
      setDevices,
      setControls,
      setDeletedControls,
      setCurves,
      setIndeNodes,
      setAggregators,
      setIndividualDevice,
      setIndividualAggregator,
      setTopoNodeData,
      setIndeNodeData,
      setTopoNodeDefaultControl,
      setIndeNodeDefaultControl,
      setIndividualCurve,
      setConfigurationOptions
    } = this.props;
    const {
      TOPONODES,
      INDENODES,
      AGGREGATORS,
      CURVES,
      CONTROLS,
      DELETED_CONTROLS,
      DEVICES,
      CONFIGURATION
    } = reduxObjectNames;
    this.pollCalls = {
      POLL_TOPONODES: {
        reduxObjectName: TOPONODES,
        fetchCurrentData: reduxObject => {
          return reduxObject.map(topoNode =>
            filterForAllowedProperties(topoNode, allowedTopoNodesProperties)
          );
        },
        fetchNewData: async params => {
          return await fetchAllTopoNodes(params);
        },
        updateData: setTopoNodes
      },
      POLL_INDENODES: {
        reduxObjectName: INDENODES,
        fetchCurrentData: reduxObject => {
          return reduxObject.map(indeNode =>
            filterForAllowedProperties(indeNode, allowedIndeNodesProperties)
          );
        },
        fetchNewData: async params => {
          return await fetchAllIndeNodes(params);
        },
        updateData: setIndeNodes
      },
      POLL_AGGREGATORS: {
        reduxObjectName: AGGREGATORS,
        fetchCurrentData: reduxObject => {
          return reduxObject.map(aggregator =>
            filterForAllowedProperties(aggregator, allowedAggregatorsProperties)
          );
        },
        fetchNewData: async params => {
          return await fetchAllAggregators(params);
        },
        updateData: setAggregators
      },
      POLL_CONTROLS_AND_RESPONSES: {
        reduxObjectName: CONTROLS,
        fetchCurrentData: reduxObject => {
          return reduxObject;
        },
        fetchNewData: async () => {
          return await fetchAllControlsAndResponses();
        },
        updateData: setControls
      },
      POLL_DELETED_CONTROLS_AND_RESPONSES: {
        reduxObjectName: DELETED_CONTROLS,
        fetchCurrentData: reduxObject => {
          return reduxObject;
        },
        fetchNewData: async params => {
          return await fetchAllControlsAndResponses({
            ...params,
            deleted: true
          });
        },
        updateData: setDeletedControls
      },
      POLL_CURVES: {
        reduxObjectName: CURVES,
        fetchCurrentData: reduxObject => {
          return reduxObject.map(curve =>
            filterForAllowedProperties(curve, allowedCurvesProperties)
          );
        },
        fetchNewData: async params => {
          return await fetchAllCurves(params);
        },
        updateData: setCurves
      },
      POLL_DEVICES: {
        reduxObjectName: DEVICES,
        fetchCurrentData: reduxObject => {
          return reduxObject.map(device =>
            filterForAllowedProperties(device, allowedDevicesProperties)
          );
        },
        fetchNewData: async params => {
          return await fetchAllDevices(params);
        },
        updateData: setDevices
      },
      POLL_CONFIGURATION: {
        reduxObjectName: CONFIGURATION,
        fetchCurrentData: reduxObject =>
          filterForAllowedProperties(
            reduxObject,
            allowedConfigurationProperties,
            true
          ),
        fetchNewData: async () => {
          const updatedConfig = await getConfigOptions();
          return filterForAllowedProperties(
            updatedConfig,
            allowedConfigurationProperties,
            true
          );
        },
        updateData: setConfigurationOptions
      },
      POLL_INDIVIDUAL_TOPONODE: {
        reduxObjectName: TOPONODES,
        fetchCurrentData: (reduxObject, resourceId) => {
          const topoNode = reduxObject.find(
            topoNode => topoNode.topoNodeId === resourceId
          );
          return filterForAllowedProperties(
            topoNode,
            allowedTopoNodesProperties
          );
        },
        fetchNewData: async resourceId => {
          return await fetchTopoNodeById(resourceId);
        },
        updateData: setTopoNodeData
      },
      POLL_INDIVIDUAL_INDENODE: {
        reduxObjectName: INDENODES,
        fetchCurrentData: (reduxObject, resourceId) => {
          const indeNode = reduxObject.find(
            indeNode => indeNode.indeNodeId === resourceId
          );
          return filterForAllowedProperties(
            indeNode,
            allowedIndeNodesProperties
          );
        },
        fetchNewData: async resourceId => {
          return await fetchIndeNodeById(resourceId);
        },
        updateData: setIndeNodeData
      },
      POLL_INDIVIDUAL_AGGREGATOR: {
        reduxObjectName: AGGREGATORS,
        fetchCurrentData: (reduxObject, resourceId) => {
          return reduxObject.find(
            aggregator => aggregator.aggregatorId === resourceId
          );
        },
        fetchNewData: async resourceId => {
          return await fetchAggregatorById(resourceId);
        },
        updateData: setIndividualAggregator
      },
      POLL_INDIVIDUAL_CURVE: {
        reduxObjectName: CURVES,
        fetchCurrentData: (reduxObject, resourceId) => {
          return reduxObject.find(curve => curve.curveId === resourceId);
        },
        fetchNewData: async resourceId => {
          return await fetchCurveById(resourceId);
        },
        updateData: setIndividualCurve
      },
      POLL_INDIVIDUAL_DEVICE: {
        reduxObjectName: DEVICES,
        fetchCurrentData: (reduxObject, resourceId) => {
          return reduxObject.find(device => device.sfdi === resourceId);
        },
        fetchNewData: async resourceId => {
          return await fetchDeviceBySFDI(resourceId);
        },
        updateData: setIndividualDevice
      },
      POLL_TOPONODE_DEFAULT_CONTROL: {
        reduxObjectName: TOPONODES,
        fetchCurrentData: (reduxObject, resourceId) => {
          return (
            reduxObject.find(topoNode => topoNode.topoNodeId === resourceId)
              .defaultControl || null
          );
        },
        fetchNewData: async resourceId => {
          return await fetchTopoNodeDefaultControl(resourceId);
        },
        updateData: setTopoNodeDefaultControl,
        requiresIdAndBodyParams: true
      },
      POLL_INDENODE_DEFAULT_CONTROL: {
        reduxObjectName: INDENODES,
        fetchCurrentData: (reduxObject, resourceId) => {
          return (
            reduxObject.find(indeNode => indeNode.indeNodeId === resourceId)
              .defaultControl || null
          );
        },
        fetchNewData: async resourceId => {
          return await fetchIndeNodeDefaultControl(resourceId);
        },
        updateData: setIndeNodeDefaultControl,
        requiresIdAndBodyParams: true
      }
    };
  }

  /**
   * Check for updates in our polling store.
   * 1. Check if there are new subscriptions
   *    >> Start the update process for those.
   * 2. Check if there are unsubscriptions.
   *    >> Clear the subscription's interval.
   * 3. Check if something inside of the poll changed (e.g. frequency or params).
   *    >> Clear the old interval for that poll and restart the polling process.
   *
   * @param {object} prevProps
   */
  componentDidUpdate(prevProps) {
    if (prevProps.polling !== this.props.polling) {
      const changedPollIds = [];

      this.props.polling.polls.forEach(poll => {
        const oldSubscription = prevProps.polling.polls.find(
          prevPoll => prevPoll.id === poll.id
        );

        //The subscription exists already. Check if any properties in here are different, except the 'lastChanged' property.
        const filteredPoll = { ...poll, lastChanged: null };
        const filteredOldSubscription = {
          ...oldSubscription,
          lastChanged: null
        };
        if (oldSubscription) {
          if (!_.isEqual(filteredPoll, filteredOldSubscription)) {
            //Only add the poll if it hasn't been added already
            if (!changedPollIds.includes(poll.id)) {
              changedPollIds.push(poll.id);
              clearInterval(this.intervals[poll.id]);
            }
          }
        }

        //New subscription
        else {
          changedPollIds.push(poll.id);
        }
      });

      //Check any unsubscriptions (old polls that are not in our new poll object)
      const newPollIds = this.props.polling.polls.map(poll => poll.id);
      prevProps.polling.polls.forEach(prevPoll => {
        if (!newPollIds.includes(prevPoll.id)) {
          if (!changedPollIds.includes(prevPoll.id)) {
            clearInterval(this.intervals[prevPoll.id]);
          }
        }
      });

      //Poll for all of the changed/new subscriptions.
      this.updateStoreWithPolls(changedPollIds);
    }
  }

  componentWillUnmount() {
    for (let interval in this.intervals) {
      clearInterval(this.intervals[interval]);
    }
  }

  /**
   * Checks each poll type in the param array.
   *
   * Checks if each pollType in the array has new data.
   * Then, set a new interval for each poll if needed.
   * @param {array} pollIds - array of poll IDs
   */
  updateStoreWithPolls = async pollIds => {
    const { updateLastUpdatedTime } = this.props;
    pollIds.forEach(async pollId => {
      const pollInfo = this.props.polling.polls.find(
        poll => poll.id === pollId
      );

      //Safeguard against possible errors
      if (!pollInfo) {
        clearInterval(this.intervals[pollId]);
        return;
      }

      let pollsChanged = false;
      for (let poll of pollInfo.types) {
        const { type, resourceId } = poll;
        const {
          fetchCurrentData,
          fetchNewData,
          updateData,
          requiresIdAndBodyParams,
          reduxObjectName
        } = this.pollCalls[type];
        const currentReduxObject = this.props[reduxObjectName];
        const oldData = await fetchCurrentData(currentReduxObject, resourceId);
        const newData = await fetchNewData(resourceId);
        //Check for differences in the previous redux data and the new fetched data
        //And if so, update the redux store.
        const hasChanged = !_.isEqual(oldData, newData);
        if (hasChanged) {
          if (requiresIdAndBodyParams) {
            await updateData(resourceId, newData);
          } else {
            await updateData(newData);
          }
          pollsChanged = true;
        }
      }
      if (pollsChanged) {
        await updateLastUpdatedTime(null, pollId);
      }

      //Set new interval for poll.
      if (!this.intervals[pollId]) {
        this.intervals[pollId] = setInterval(() => {
          this.updateStoreWithPolls([pollId]);
        }, pollInfo.frequency);
      }
    });
  };

  render() {
    return (
      // TODO: Visual Component for Polling
      <div />
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    [reduxObjectNames.TOPONODES]: state.topoNode.topoNodes,
    [reduxObjectNames.INDENODES]: state.indeNode.indeNodes,
    [reduxObjectNames.AGGREGATORS]: state.aggregator.aggregators,
    [reduxObjectNames.CONTROLS]: state.control.controls,
    [reduxObjectNames.DELETED_CONTROLS]: state.control.deletedControls,
    [reduxObjectNames.CURVES]: state.curve.curves,
    [reduxObjectNames.DEVICES]: state.device.devices,
    [reduxObjectNames.POLLING]: state.polling,
    [reduxObjectNames.CONFIGURATION]: state.device.configuration
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setTopoNodes,
      setDevices,
      setDeletedControls,
      setControls,
      setCurves,
      setIndeNodes,
      setAggregators,
      setIndividualDevice,
      updateLastUpdatedTime,
      setIndividualAggregator,
      setTopoNodeData,
      setIndeNodeData,
      setTopoNodeDefaultControl,
      setIndeNodeDefaultControl,
      setIndividualCurve,
      setConfigurationOptions
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Polling);
